/*
=========================================================
* RingFoods FAQ Section
=========================================================
*/

import { useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";

// @mui icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, grey300 } from "pages/RingFoodsHomepage/theme";

// Exported so index.js can emit FAQPage JSON-LD from the SAME array that
// renders the visible accordion. Google requires FAQ markup to correspond to
// Q&A shown on the page; deriving both from one source makes that true by
// construction rather than by remembering to keep two lists in sync.
export const faqs = [
  {
    // Definitional question, deliberately first. "AI receptionist" and
    // "restaurant answering service" are phrases the Ads account pays for and
    // that the site previously never used in body copy — this is the one place
    // on the homepage where saying them plainly is also the most useful answer.
    q: "Is RingFoods an AI receptionist or an answering service?",
    a: "It's an AI voice agent for restaurants — closer to an AI receptionist than to a traditional answering service. A human answering service takes a message and passes it on; RingFoods actually finishes the call: it checks your real table availability and books the reservation, takes a takeout order, answers menu and hours questions, and only transfers to your team when a caller needs a person.",
  },
  {
    q: "Does the AI really sound human?",
    a: "Yes — studio-quality neural voices with natural pacing and tone. Choose from 10 premium AI voices and preview your greeting right in the dashboard. Most callers never realize they're talking to AI.",
  },
  {
    q: "Can I keep my existing phone number?",
    a: "Yes. Forward your number to RingFoods on no-answer, after-hours, or 24/7. Your number stays on your signage, menus, and Google listing.",
  },
  {
    q: "What languages does it support?",
    a: "The AI auto-detects the caller's language and responds natively — Mandarin, Spanish, French, Arabic, and many more. Menu OCR extraction also reads menus in 9+ languages.",
  },
  {
    q: "What's the free restaurant webpage?",
    a: "Every plan includes a public page at www.ringfoods.com/your-restaurant with your menu, photos, and hours. Guests can book tables, place takeout and delivery orders, join your waitlist, and leave feedback online — and you get a printable QR poster for your window or counter. It goes live automatically with your phone number.",
  },
  {
    q: "How does the waitlist work?",
    a: "When you're fully booked, guests join the waitlist by phone or from your webpage. Same-day guests see their live position in line; future-date guests are matched to openings. The moment a table frees up, the system texts them automatically — and on Professional and Enterprise plans, the AI can even call them with the open spot.",
  },
  {
    q: "What happens if the AI can't help?",
    a: "It transfers the call to a human on your team — you set the transfer number and rules. Every call is recorded and transcribed, so you can review exactly what happened afterward.",
  },
  {
    q: "Do you block spam calls?",
    a: "Yes. Numbers on your spam blocklist are hung up on before the AI even answers, so robocalls and telemarketers never waste your minutes. You manage the list yourself from the dashboard.",
  },
  {
    q: "How long does setup take?",
    a: "About 30 minutes. Enter your business details and hours, snap a photo of your menu for AI extraction, pick a voice, and forward your number. No IT team needed.",
  },
];

function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <MKBox component="section" py={{ xs: 8, md: 12 }} id="faq">
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 5, md: 7 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Frequently Asked Questions
            </MKTypography>
            <MKTypography
              variant="body1"
              color="text"
              sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}
            >
              Everything restaurant owners ask us before starting their free trial. Didn&apos;t find
              your answer? Email{" "}
              <MKBox
                component="a"
                href="mailto:support@ringfoods.com"
                sx={{ color: brandPrimary, fontWeight: 600, textDecoration: "underline" }}
              >
                support@ringfoods.com
              </MKBox>
            </MKTypography>
          </Grid>
        </Grid>

        {/* Accordion list */}
        <Grid container>
          <Grid item xs={12} lg={9} mx="auto">
            <MKBox sx={{ borderTop: `1px solid ${grey300}` }}>
              {faqs.map((faq, index) => {
                const isOpen = open === index;
                return (
                  <MKBox key={index} sx={{ borderBottom: `1px solid ${grey300}` }}>
                    <MKBox
                      component="button"
                      onClick={() => setOpen(isOpen ? -1 : index)}
                      sx={{
                        all: "unset",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        gap: 2,
                        py: { xs: 2, md: 2.5 },
                        transition: "background 180ms ease",
                        "&:hover": { backgroundColor: "rgba(26, 26, 26, 0.02)" },
                      }}
                    >
                      <MKTypography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "1rem", md: "1.15rem" }, textAlign: "left" }}
                      >
                        {faq.q}
                      </MKTypography>
                      <ExpandMoreIcon
                        sx={{
                          color: brandPrimary,
                          flexShrink: 0,
                          transform: isOpen ? "rotate(180deg)" : "none",
                          transition: "transform 180ms ease",
                        }}
                      />
                    </MKBox>
                    <Collapse in={isOpen}>
                      <MKTypography
                        variant="body2"
                        color="text"
                        sx={{
                          pb: { xs: 2, md: 2.5 },
                          lineHeight: 1.7,
                          opacity: 0.85,
                          maxWidth: 680,
                        }}
                      >
                        {faq.a}
                      </MKTypography>
                    </Collapse>
                  </MKBox>
                );
              })}
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default FAQ;
